import { forwardRef, useCallback, useEffect, useImperativeHandle, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../../../utils/api";
import { LoginHelper } from "../../../../utils/LoginHelper";
import DetailAccentCard, {
	DetailSectionHeading,
} from "../../../common/detail/DetailAccentCard";
import Loading from "../../../common/loading/Loading";
import {
	isWebinarSpeaker,
	userHasWebinarJoinAccess,
	WEBINAR_EVENT_PAST_MESSAGE,
	WEBINAR_JOIN_ENDED_REGISTERED_MESSAGE,
	webinarHasStarted,
} from "../webinarAccess";

const TIME_HINT_MESSAGES = new Set([
	"وبینار هنوز شروع نشده است",
	"وبینار در حال اجرا نیست",
]);

function mapTimeHintForRegistered(message) {
	if (message === "وبینار در حال اجرا نیست") {
		return WEBINAR_JOIN_ENDED_REGISTERED_MESSAGE;
	}
	return message;
}

const JoinSection = forwardRef(({ webinar }, ref) => {
	const [moderator_url, setModeratorUrl] = useState("");
	const [attendee_url, setAttendeeUrl] = useState("");
	const [errorMessage, setErrorMessage] = useState("");
	const [infoMessage, setInfoMessage] = useState("");
	const [isLoading, setIsLoading] = useState(true);

	const requestJoinUrl = useCallback(
		({ asSpeaker }) => {
			setIsLoading(true);
			const request = asSpeaker
				? api.webinarModeratorUrl({
						token: LoginHelper.getToken(),
						webinar_id: webinar.id,
					})
				: api.webinarAttendeeUrl({
						token: LoginHelper.getToken(),
						webinar_id: webinar.id,
					});

			return request
				.then((result) => {
					if (asSpeaker) {
						setModeratorUrl(result.moderator_url);
						setAttendeeUrl("");
					} else {
						setAttendeeUrl(result.attendee_url);
						setModeratorUrl("");
					}
					setErrorMessage("");
					setIsLoading(false);
				})
				.catch((err) => {
					const message = err?.data?.message ?? "";
					const registered = userHasWebinarJoinAccess(webinar);
					if (TIME_HINT_MESSAGES.has(message)) {
						setInfoMessage(
							registered
								? mapTimeHintForRegistered(message)
								: message,
						);
					} else {
						setErrorMessage(message || "امکان دریافت لینک ورود نیست.");
					}
					setIsLoading(false);
				});
		},
		[webinar],
	);

	const fetchJoinUrl = useCallback(() => {
		setModeratorUrl("");
		setAttendeeUrl("");
		setErrorMessage("");
		setInfoMessage("");

		if (!LoginHelper.isLoggedIn()) {
			setIsLoading(false);
			setInfoMessage("برای ورود به وبینار ابتدا وارد حساب کاربری شوید.");
			return;
		}

		const hasAccess = userHasWebinarJoinAccess(webinar);
		const speaker = isWebinarSpeaker(webinar);
		const started = webinarHasStarted(webinar);

		if (!hasAccess) {
			if (started) {
				setIsLoading(true);
				api.webinarAttendeeUrl({
					token: LoginHelper.getToken(),
					webinar_id: webinar.id,
				})
					.then((result) => {
						setAttendeeUrl(result.attendee_url);
						setInfoMessage("");
						setIsLoading(false);
					})
					.catch((err) => {
						const message = err?.data?.message ?? "";
						if (TIME_HINT_MESSAGES.has(message)) {
							setInfoMessage(WEBINAR_JOIN_ENDED_REGISTERED_MESSAGE);
						} else {
							setInfoMessage(WEBINAR_EVENT_PAST_MESSAGE);
						}
						setIsLoading(false);
					});
				return;
			}

			setIsLoading(false);
			setInfoMessage(
				"برای ورود به وبینار، ابتدا ثبت‌نام یا خرید را تکمیل کنید.",
			);
			return;
		}

		requestJoinUrl({ asSpeaker: speaker });
	}, [webinar, requestJoinUrl]);

	useImperativeHandle(ref, () => ({
		reload() {
			fetchJoinUrl();
		},
	}));

	useEffect(() => {
		fetchJoinUrl();
	}, [fetchJoinUrl]);

	const showPurchaseLink =
		infoMessage &&
		!userHasWebinarJoinAccess(webinar) &&
		!webinarHasStarted(webinar) &&
		webinar.is_enrollable;

	return (
		<DetailAccentCard ariaLabel="ورود به وبینار" bodyClassName="p-5 md:p-6">
			<DetailSectionHeading className="mb-4">ورود به وبینار</DetailSectionHeading>
			{isLoading ? (
					<div className="flex justify-center py-4">
						<Loading color="#ff694b" width={28} />
					</div>
				) : (
					<div className="flex flex-col items-center gap-3">
						{moderator_url && (
							<Link
								to={moderator_url}
								target="_blank"
								rel="noopener noreferrer"
								className="w-full max-w-[280px] rounded-[10px] bg-cta py-3 text-center font-Vazir-700 text-[15px] text-white no-underline transition hover:opacity-90"
							>
								ورود سخنران
							</Link>
						)}
						{attendee_url && (
							<Link
								to={attendee_url}
								target="_blank"
								rel="noopener noreferrer"
								className="w-full max-w-[280px] rounded-[10px] bg-cta py-3 text-center font-Vazir-700 text-[15px] text-white no-underline transition hover:opacity-90"
							>
								ورود مهمان
							</Link>
						)}
						{infoMessage && (
							<p className="max-w-[360px] text-center font-Vazir-500 text-[14px] text-darkBlue/75">
								{infoMessage}
							</p>
						)}
						{showPurchaseLink && (
							<Link
								to={`/webinar_purchase/${webinar.id}/`}
								className="font-Vazir-600 text-[14px] text-cta no-underline hover:underline"
							>
								رفتن به صفحه ثبت‌نام / خرید
							</Link>
						)}
						{!LoginHelper.isLoggedIn() && (
							<Link
								to="/login/"
								className="font-Vazir-600 text-[14px] text-cta no-underline hover:underline"
							>
								ورود به حساب
							</Link>
						)}
						{errorMessage && (
							<p className="max-w-[320px] text-center font-Vazir-500 text-[14px] text-[#cc0000]">
								{errorMessage}
							</p>
						)}
					</div>
				)}
		</DetailAccentCard>
	);
});

export default JoinSection;
